import { useEffect, useRef, useState } from 'react';
import { useToast, type Toast, DEFAULT_TOAST_DURATION } from '@/context/ToastContext';

const ICONS: Record<Toast['type'], string> = {
  success: 'ri-checkbox-circle-fill',
  error: 'ri-close-circle-fill',
  info: 'ri-information-fill',
  warning: 'ri-alert-fill',
};

const COLORS: Record<Toast['type'], string> = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  info: 'bg-sky-50 border-sky-200 text-sky-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
};

const ICON_COLORS: Record<Toast['type'], string> = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  info: 'text-sky-500',
  warning: 'text-amber-500',
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

function ToastItem({ toast, onRemove }: ToastItemProps) {
  const [visible, setVisible] = useState(false);
  const duration = toast.duration ?? DEFAULT_TOAST_DURATION;
  const remainingRef = useRef(duration);
  const timerStartRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    timerStartRef.current = Date.now();
    timerRef.current = setTimeout(() => onRemove(toast.id), remainingRef.current);
  };

  useEffect(() => {
    if (duration > 0) startTimer();
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le toast ne disparait pas tant que le curseur est dessus — le decompte
  // reprend avec le temps restant (pas remis a zero) des que le curseur le quitte.
  const handleMouseEnter = () => {
    if (!timerRef.current) return;
    remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - timerStartRef.current));
    clearTimer();
  };

  const handleMouseLeave = () => {
    if (duration <= 0) return;
    if (remainingRef.current <= 0) {
      onRemove(toast.id);
      return;
    }
    startTimer();
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg min-w-[300px] max-w-[420px] transition-all duration-300 ${
        visible
          ? 'translate-x-0 opacity-100'
          : 'translate-x-8 opacity-0'
      } ${COLORS[toast.type]}`}
    >
      <div className={`flex-shrink-0 ${ICON_COLORS[toast.type]}`}>
        <i className={`${ICONS[toast.type]} text-lg`} />
      </div>
      <p className="text-sm font-medium flex-1 leading-snug">{toast.message}</p>
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
        aria-label="Fermer"
      >
        <i className="ri-close-line" />
      </button>
    </div>
  );
}

export default function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col-reverse gap-3">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  );
}